import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { VatBreakdown } from "@/components/VatBreakdown";
import { Sparkles } from "lucide-react";
import { formatMoney } from "@/lib/format";
import { Field } from "./Field";
import { CURRENCIES, to2 } from "./receiptUtils";

export function ExtractedDataFields({ form, setField, step, setVatBreakdown }) {
  const disabled = step === "extracting";
  return (
    <section className="border border-border bg-card p-5 space-y-4">
      <div className="flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-primary" />
        <h2 className="font-heading text-lg font-medium">Ausgelesene Daten</h2>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Lieferant">
          <Input
            value={form.supplier}
            onChange={(e) => setField("supplier", e.target.value)}
            placeholder="z. B. REWE"
            className="bg-white"
            data-testid="input-supplier"
            disabled={disabled}
          />
        </Field>
        <Field label="Rechnungsnummer">
          <Input
            value={form.invoice_number}
            onChange={(e) => setField("invoice_number", e.target.value)}
            placeholder="z. B. 2024-0815"
            className="bg-white"
            data-testid="input-invoice-number"
            disabled={disabled}
          />
        </Field>
        <Field label="Datum">
          <Input
            type="date"
            value={form.date}
            onChange={(e) => setField("date", e.target.value)}
            className="bg-white"
            data-testid="input-date"
            disabled={disabled}
          />
        </Field>
        <Field label="Preis (Brutto)">
          <Input
            type="number"
            step="0.01"
            value={form.price}
            onChange={(e) => setField("price", e.target.value)}
            onBlur={(e) => setField("price", to2(e.target.value))}
            placeholder="0,00"
            className="bg-white tabular"
            data-testid="input-price"
            disabled={disabled}
          />
        </Field>
        <Field label="Währung">
          <Select value={form.currency} onValueChange={(v) => setField("currency", v)}>
            <SelectTrigger className="bg-white" data-testid="select-currency">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CURRENCIES.map((c) => (
                <SelectItem key={c} value={c}>
                  {c}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label="MwSt. Betrag">
          <Input
            type="number"
            step="0.01"
            value={form.vat_amount}
            onChange={(e) => setField("vat_amount", e.target.value)}
            onBlur={(e) => setField("vat_amount", to2(e.target.value))}
            placeholder="0,00"
            className="bg-white tabular"
            data-testid="input-vat"
            disabled={disabled}
          />
        </Field>
        <Field label="MwSt. Satz (%)">
          <Input
            type="number"
            step="0.1"
            value={form.vat_rate}
            onChange={(e) => setField("vat_rate", e.target.value)}
            placeholder="19"
            className="bg-white tabular"
            data-testid="input-vat-rate"
            disabled={disabled}
          />
        </Field>
        <Field label="Trinkgeld">
          <Input
            type="number"
            step="0.01"
            value={form.tip}
            onChange={(e) => setField("tip", e.target.value)}
            onBlur={(e) => setField("tip", to2(e.target.value))}
            placeholder="0,00"
            className="bg-white tabular"
            data-testid="input-tip"
            disabled={disabled}
          />
        </Field>
      </div>

      {(Number(form.tip) || 0) > 0 && (
        <div
          className="flex justify-between items-baseline border-t border-border pt-3"
          data-testid="editor-grand-total"
        >
          <span className="data-label">Gesamtbetrag (inkl. Trinkgeld)</span>
          <span className="text-base font-semibold tabular">
            {formatMoney((Number(form.price) || 0) + (Number(form.tip) || 0), form.currency)}
          </span>
        </div>
      )}

      {form.vat_breakdown?.length >= 2 && (
        <div className="space-y-2 pt-1" data-testid="editor-vat-breakdown">
          <VatBreakdown
            lines={form.vat_breakdown}
            currency={form.currency}
            editable
            onChange={setVatBreakdown}
          />
        </div>
      )}
    </section>
  );
}
